import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { branchService } from '@/data/branchService';
import { useUserViewMode } from '@/hooks/useUserViewMode';

interface Props {
  branchId: number;
}

/**
 * Şube bazlı bakiye anahtarı (Branch.balanceTopUpEnabled). Kapalıyken bu
 * şubede bakiye yükleme, bakiyeyle ödeme ve bakiyeyle giriş sunucuda
 * reddedilir; uygulamalar ve web POS ilgili seçenekleri gizler.
 * Değiştirme yetkisi backend'de: SUPER_ADMIN, COMPANY_ADMIN (kendi şirketi),
 * BRANCH_ADMIN (kendi şubesi).
 */
const BranchBalanceSettingsCard = ({ branchId }: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { isSuperAdmin, isCompanyAdmin, isBranchAdmin } = useUserViewMode();
  const canManage = isSuperAdmin || isCompanyAdmin || isBranchAdmin;

  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await branchService.getBalanceSettings(branchId);
      setEnabled(result.balanceTopUpEnabled);
    } catch (err) {
      console.error('Error loading branch balance settings:', err);
      setError(t('branch.balance.loadError'));
    } finally {
      setLoading(false);
    }
  }, [branchId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (checked: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const result = await branchService.updateBalanceSettings(
        branchId,
        checked
      );
      setEnabled(result.balanceTopUpEnabled);
      enqueueSnackbar(
        t(
          result.balanceTopUpEnabled
            ? 'branch.balance.enabledToast'
            : 'branch.balance.disabledToast'
        ),
        { variant: 'success' }
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          t('branch.balance.saveError')
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
          <AccountBalanceWalletIcon color="primary" />
          <Typography variant="h5">{t('branch.balance.title')}</Typography>
          {(loading || saving) && <CircularProgress size={18} />}
        </Stack>

        <Typography variant="body2" color="text.secondary" mb={1.5}>
          {t('branch.balance.description')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}

        {enabled !== null && (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  disabled={!canManage || saving}
                  onChange={(event) => handleToggle(event.target.checked)}
                />
              }
              label={t(
                enabled
                  ? 'branch.balance.switchLabelOn'
                  : 'branch.balance.switchLabelOff'
              )}
              slotProps={{ typography: { variant: 'body2' } }}
              sx={{ m: 0 }}
            />

            {!enabled && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {t('branch.balance.offWarning')}
              </Alert>
            )}

            {!canManage && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={1}
              >
                {t('branch.balance.readOnly')}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BranchBalanceSettingsCard;
